import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { omit } from 'lodash'
import { Card } from '@mui/material'
import PageContainer from 'components/PageContainer'
import PageWrapper from 'components/PageWrapper'
import Suspense from 'components/Suspense'
import { buildQueryParams } from 'services/router'
import JobPosting from 'components/JobPosting'
import { trackError, trackEvent } from 'services/analytics'
import { useIsGuest, useIsMasked, useIsMember } from 'hooks/user'
import { useApplyToJob } from 'hooks/job'
import { useQueryParams } from 'hooks/router'
import JobClientActions from 'scenes/Job/components/JobClientActions'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { Job_User$key } from '__generated__/Job_User.graphql'
import { Job_Job$key } from '__generated__/Job_Job.graphql'
import { useRouter } from 'next/router'
import { useQueryLoader } from 'react-relay'
import { JobApplicationDialogQuery } from 'scenes/Job/components/JobApplicationDialog/JobApplicationDialog'
import { JobApplicationDialog_Query } from '__generated__/JobApplicationDialog_Query.graphql'
import { useDebouncedCallback } from 'use-debounce'
import JobApplicationDialog from './components/JobApplicationDialog'
import JobHeader from './components/JobHeader'
import JobFooter from './components/JobFooter'
import useStyles from './styles'
import JobHead from './components/JobHead'
import FakeApplyDialog from './components/JobApplicationDialog/components/FakeApplyDialog/FakeApplyDialog'

interface IJobProps {
  userFragmentRef: Job_User$key | null
  jobFragmentRef: Job_Job$key
}

function Job({ userFragmentRef, jobFragmentRef }: IJobProps) {
  const { classes } = useStyles()
  const job = useFragment(graphql`
    fragment Job_Job on Job {
      slug
      title
      firm {
        slug
        name
      }
      contract {
        status
      }
      
      ...JobHead_Job
      ...JobHeader_Job
      ...JobPosting_Job
      ...JobClientActions_Job
      ...JobFooter_Job
      
      ...job_useApplyToJob
    }
  `, jobFragmentRef)

  const user = useFragment(graphql`
    fragment Job_User on User {
      status
      workingOrOfferedJob
      
      ...user_useIsMasked
      ...user_useIsMember
      ...user_useIsGuest
      
      ...JobHeader_User
      ...JobClientActions_User
      ...JobFooter_User
    }
  `, userFragmentRef)

  const router = useRouter()
  const [applying, setApplying] = useState(false)
  const [applicationDialogClosedByUser, setApplicationDialogClosedByUser] = useState(false)
  const [fakeDialogOpen, setFakeDialogOpen] = useState(false)
  const query = useQueryParams()

  const isGuest = useIsGuest(user)
  const isMember = useIsMember(user)
  const isMasked = useIsMasked(user)

  // Handle job application dialog data
  const [jobApplicationDialogQuery, loadJobApplication] = useQueryLoader<JobApplicationDialog_Query>(JobApplicationDialogQuery)

  useEffect(() => {
    if (job?.slug && job?.firm?.slug) {
      // NOTE: using network-only policy because if we are coming from a profile edit,
      // we might end up using stale data and show the wrong dialog (and/or the wrong content)
      loadJobApplication({ jobSlug: job.slug, firmSlug: job.firm.slug }, { fetchPolicy: 'network-only' })
    }
  }, [job?.firm?.slug, job?.slug])

  const title = useMemo(() => (
    job?.firm ? `${job.title} at ${job.firm.name}${job.firm.name !== 'Flexhire' ? ' - Flexhire' : ''}` : 'Flexhire'
  ), [job])

  const { apply } = useApplyToJob(job)

  const autoApplying = useMemo(() => (
    job && query.applying === 'true' && !applicationDialogClosedByUser && !isMasked && !user?.workingOrOfferedJob
  ), [job, query, applicationDialogClosedByUser, isMasked, user])

  const openFakeApplyDialog = useCallback(() => {
    setFakeDialogOpen(true)
  }, [])

  const closeFakeApplyDialog = useCallback(() => {
    setFakeDialogOpen(false)
  }, [])

  const openApplyDialog = useCallback(() => {
    // Handle signed out users, and users that haven't submitted their profile yet
    if (isGuest) {
      router.push(`/signup/member?job=${job.slug}&firm=${job.firm.slug}`)
    } else if (user?.status === 'pending') {
      router.push(`/profile?job=${job.slug}&firm=${job.firm.slug}`)
    } else {
      trackEvent('Job Application Dialog Opened')
      setApplying(true)
    }
  }, [user?.status, job?.slug, job?.firm?.slug, isGuest, router])

  const closeApplyDialog = useCallback(() => {
    // Prevent infinite loop (not sure how it happens)
    if (!applying && applicationDialogClosedByUser) return

    setApplicationDialogClosedByUser(true)
    setApplying(false)

    if (query.applying === 'true') {
      let params = buildQueryParams(omit(query, 'applying'))

      if (isGuest || isMember) {
        router.replace('/[...slugs]', `/${job.firm?.slug || 'job'}/${job.slug}${params}`, { shallow: true })
      }
    }

    trackEvent('Job Application Dialog Closed')
  }, [query, applying, applicationDialogClosedByUser, job])

  const canSendJobView = job?.slug && !job?.contract?.status && !isMasked && (isGuest || isMember)

  const sendJobViewImmediate = useCallback(() => {
    if (canSendJobView) {
      apply(null).catch(err => trackError(err))
    }
  }, [canSendJobView, apply])

  const sendJobView = useDebouncedCallback(sendJobViewImmediate, 300, {
    trailing: true,
  })

  useEffect(() => {
    if (canSendJobView) sendJobView()
  }, [canSendJobView])

  useEffect(() => {
    if ((!job || !query.applying) && autoApplying && !applying) openApplyDialog()
  }, [job, query?.applying, autoApplying, applying])

  return (
    <div>
      <JobHead
        jobFragmentRef={job}
        title={title}
      />

      <Suspense>
        {jobApplicationDialogQuery && (
          <JobApplicationDialog
            preloadedQuery={jobApplicationDialogQuery}
            open={applying || autoApplying}
            onClose={closeApplyDialog}
          />
        )}
      </Suspense>

      {fakeDialogOpen && (
        <FakeApplyDialog
          open={fakeDialogOpen}
          close={closeFakeApplyDialog}
        />
      )}

      <JobHeader
        jobFragmentRef={job}
        userFragmentRef={user}
        apply={openApplyDialog}
        fakeApply={openFakeApplyDialog}
      />

      <div className={classes.mainArea}>
        <PageContainer>
          <PageWrapper style={{ position: 'relative' }} withoutCard noAnim>
            <Card raised>
              <JobPosting
                jobFragmentRef={job}
              />
              <JobClientActions
                userFragmentRef={user}
                jobFragmentRef={job}
              />
            </Card>

            <JobFooter
              jobFragmentRef={job}
              userFragmentRef={user}
              apply={openApplyDialog}
              fakeApply={openFakeApplyDialog}
            />
          </PageWrapper>
        </PageContainer>
      </div>
    </div>
  )
}

export default memo(Job)
